import type { RankTarget } from "@/constants";
import type { TravelMode } from "@/repo/anchors";
import { getEnvParam } from "@/env";

export type RankMethod = "wpm" | "wsm" | "power_mean";

export const RANK_METHOD_LABELS: Record<RankMethod, string> = {
    wpm: "Weighted product (non-compensatory)",
    wsm: "Weighted sum (linear)",
    power_mean: "Power mean (tunable)",
};

/** Default `p` for the power-mean method; mirrors the server default. */
export const POWER_MEAN_DEFAULT_P = -2;

/** One property room bucket: count of local properties with a habitable-room
 * count (10 = "10 or more"). */
export type PropertyBucket = {
    bandIndex: number;
    rooms: number;
    count: number;
};

/** One price bucket: count of local properties in a price range. `minPrice`/
 * `maxPrice` are null for the open-ended first/last buckets. */
export type FinanceBucket = {
    minPrice: number | null;
    maxPrice: number | null;
    count: number;
};

/** A clicked cell/place's full score breakdown, shown in the side panel. */
export type ScoreBreakdown = {
    name: string;
    value: number;
    location: number;
    finance: number | null;
    property: number | null;
    matched: number | null;
    total: number | null;
    attributes: { name: string; value: number }[];
    /** Per-bucket property counts (place/cell clicks; empty otherwise). */
    buckets: PropertyBucket[];
    /** The household's required-rooms band, to highlight its bucket. */
    householdBand: number | null;
    /** Per-price-range property counts (place clicks only; empty otherwise). */
    financeBuckets: FinanceBucket[];
    /** The budget's own price-bucket index, to highlight it. */
    financeBucket: number | null;
    /** Local average sale price (token/cell clicks only; null for places). */
    averagePrice: number | null;
    /** False when the on-demand query failed, so only the tile value is known. */
    explained: boolean;
};

export type RankingContext = {
    // Attribute *names* — this object is JSON.stringified by the map's
    // transformRequest hook and sent as the POST body to /api/tiles/rank/...,
    // which expects names (not ids).
    stack_rank: string[];
    max_borrowing_potential?: number;
    method?: RankMethod;
    /** Exponent for the power-mean method; ignored otherwise. */
    p?: number;
    /** What's being ranked — drives the tile URL the explorer hits. */
    target?: RankTarget;
    /** Place level when target = "places". */
    place_level?: number;
    /** Anchor locations whose isochrones multiply token scores. */
    anchors?: { lat: number; lng: number; modes: TravelMode[] }[];
    /** Household composition driving the property size/room match score. */
    household?: {
        adults: number;
        child_0_5: number;
        child_6_12: number;
        child_13_plus: number;
    };
};

/** JSON shape returned by the tile-server `/explain/*` endpoints. */
type ExplainResponse = {
    name: string;
    value: number;
    location: number;
    finance: number | null;
    property: number | null;
    matched: number | null;
    total: number | null;
    attributes: Record<string, number>;
    buckets?: {
        band_index: number;
        rooms: number;
        count: number;
    }[];
    household_band?: number | null;
    finance_buckets?: {
        min_price: number | null;
        max_price: number | null;
        count: number;
    }[];
    finance_bucket?: number | null;
    average_price?: number | null;
};

function toScoreBreakdown(
    response: ExplainResponse,
    stackRank: string[],
): ScoreBreakdown {
    // Order location attributes by their position in the stack rank (the order
    // the user prioritised them), not by descending score. Names not in the
    // stack rank sort to the end.
    const rankIndex = (name: string) => {
        const i = stackRank.indexOf(name);
        return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    return {
        name: response.name,
        value: response.value,
        location: response.location,
        finance: response.finance,
        property: response.property,
        matched: response.matched,
        total: response.total,
        attributes: Object.entries(response.attributes ?? {})
            .map(([name, value]) => ({ name, value: Number(value) }))
            .sort((a, b) => rankIndex(a.name) - rankIndex(b.name)),
        buckets: (response.buckets ?? []).map((bucket) => ({
            bandIndex: bucket.band_index,
            rooms: bucket.rooms,
            count: bucket.count,
        })),
        householdBand: response.household_band ?? null,
        financeBuckets: (response.finance_buckets ?? []).map((bucket) => ({
            minPrice: bucket.min_price,
            maxPrice: bucket.max_price,
            count: bucket.count,
        })),
        financeBucket: response.finance_bucket ?? null,
        averagePrice: response.average_price ?? null,
        explained: true,
    };
}

async function postExplain(
    path: string,
    context: RankingContext,
): Promise<ScoreBreakdown | null> {
    const res = await fetch(
        `${process.env.BUN_PUBLIC_TILE_SERVER}/api/tiles/${path}`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-API-KEY": process.env.BUN_PUBLIC_TILE_SERVER_API_KEY,
            },
            body: JSON.stringify(context),
        },
    );
    if (!res.ok) return null;
    return toScoreBreakdown(
        (await res.json()) as ExplainResponse,
        context.stack_rank,
    );
}

/** Score breakdown for the S2 cell containing (lng, lat) at map zoom `z`. */
export function explainCell(
    z: number,
    lng: number,
    lat: number,
    context: RankingContext,
): Promise<ScoreBreakdown | null> {
    const query = `z=${Math.floor(z)}&lng=${lng}&lat=${lat}&${getEnvParam()}`;
    return postExplain(`explain/cell?${query}`, context);
}

/** Score breakdown for a single place by id. */
export function explainPlace(
    placeId: number,
    context: RankingContext,
): Promise<ScoreBreakdown | null> {
    return postExplain(`explain/place/${placeId}?${getEnvParam()}`, context);
}

/** Score breakdown for a single property (plot) by id, at map zoom `z`. */
export function explainProperty(
    propertyId: number,
    z: number,
    context: RankingContext,
): Promise<ScoreBreakdown | null> {
    return postExplain(
        `explain/property/${propertyId}?z=${Math.floor(z)}&${getEnvParam()}`,
        context,
    );
}
